import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Box,
  Divider,
  Stack,
  Container,
  Skeleton,
  useMediaQuery,
  useTheme,
  Slider,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase-config';
import {
  Star,
  LocalGasStation,
  Speed,
  CalendarToday,
  LocationOn,
  DirectionsCar,
  AutoAwesome,
  Whatshot
} from '@mui/icons-material';

const PremiumListings = () => {
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [transmission, setTransmission] = useState('');
  const [city, setCity] = useState('');
  const [carStatus, setCarStatus] = useState('');
  const [premiumCars, setPremiumCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const carTypes = ['Седан', 'SUV', 'Хечбек', 'Купе', 'Кабриолет'];
  const cities = [
    'Скопје', 'Битола', 'Прилеп', 'Куманово', 'Тетово', 'Охрид', 'Велес', 'Штип', 'Кавадарци', 'Гостивар',
    'Струмица', 'Неготино', 'Кочани', 'Кичево', 'Ресен', 'Гевгелија', 'Струга', 'Делчево', 'Виница', 'Кратово',
    'Крива Паланка', 'Берово', 'Македонски Брод', 'Дебар', 'Демир Капија', 'Демир Хисар', 'Пробиштип', 'Свети Николе',
    'Валандово', 'Богданци', 'Пехчево', 'Радовиш', 'Теарце', 'Желино', 'Липково', 'Чаир', 'Аеродром', 'Гази Баба',
    'Карпош', 'Кисела Вода', 'Центар', 'Ѓорче Петров', 'Сарај', 'Студеничани', 'Арачиново', 'Сопиште', 'Зелениково',
    'Илинден', 'Петровец', 'Чучер-Сандево', 'Зрновци', 'Маврово и Ростуше', 'Ранковце', 'Лозово', 'Новци', 'Новаци',
    'Конче', 'Дојран', 'Василево', 'Босилово', 'Ново Село', 'Карачиново', 'Куклиш', 'Могила', 'Пласница', 'Центар Жупа'
  ];
  const transmissions = ['Автоматски', 'Мануелен'];
  const statuses = ['Нов', 'Користен', 'Изнајмување'];

  useEffect(() => {
    const fetchPremiumCars = async () => {
      try {
        const q = query(collection(db, 'ads'), where('isPremium', '==', true));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        data.sort((a, b) => {
          if (a.isUrgent && !b.isUrgent) return -1;
          if (!a.isUrgent && b.isUrgent) return 1;
          if (a.isPremium && !b.isPremium) return -1;
          if (!a.isPremium && b.isPremium) return 1;
          return 0;
        });

        setPremiumCars(data);
      } catch (err) {
        console.error('Грешка при вчитување:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPremiumCars();
  }, []);

  const filteredCars = premiumCars.filter(car => {
    const price = parseInt(car.price?.toString().replace(/[^\d]/g, '') || 0);
    return (
      price >= priceRange[0] && price <= priceRange[1] &&
      (selectedTypes.length === 0 || selectedTypes.includes(car.type)) &&
      (!city || car.city === city) &&
      (!transmission || car.transmission === transmission) &&
      (!carStatus || car.status === carStatus)
    );
  });

  const LoadingSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(12)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
          <Box sx={{ pt: 2 }}>
            <Skeleton width="80%" height={24} />
            <Skeleton width="60%" height={20} sx={{ mt: 1 }} />
            <Skeleton width="40%" height={20} sx={{ mt: 1 }} />
            <Skeleton width="100%" height={40} sx={{ mt: 2, borderRadius: 1 }} />
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  const AdSpace = ({ size, vertical = false }) => (
    <Box sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.02)',
      border: '1px dashed rgba(0,0,0,0.1)',
      borderRadius: 1,
      textAlign: 'center',
      ...(vertical ? { 
        height: '100%',
        minHeight: 600,
        width: 160
      } : {
        width: '100%',
        height: 90,
        mb: 4
      })
    }}>
      <Typography variant="caption" color="text.secondary">
        Google Ad: {size}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      {/* Top Leaderboard Ad */}
      {!isMobile && (
        <Box sx={{ mb: 6 }}>
          <AdSpace size="970x90 (Leaderboard)" />
        </Box>
      )}

      {/* Mobile Top Ad */}
      {isMobile && (
        <Box sx={{ mb: 4 }}>
          <AdSpace size="320x100 (Mobile Banner)" />
        </Box>
      )}

      {/* Hero Header */}
      <Box sx={{
        mb: 6,
        textAlign: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
        p: 4,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            icon={<AutoAwesome />}
            label="ПРЕМИУМ КОЛЕКЦИЈА"
            color="primary"
            sx={{ mb: 2, fontWeight: 900, letterSpacing: 1 }}
          />
          <Typography variant="h3" sx={{ 
            fontWeight: 700,
            mb: 1.5,
            background: 'linear-gradient(45deg, #121212 30%, #434343 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
        
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Left Sidebar with Filters and Ad */}
        {!isMobile && (
          <Grid item md={3}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              <Paper elevation={0} sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.08)',
                mb: 3
              }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Филтрирај</Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ mb: 3 }}>
                  <Typography gutterBottom sx={{ fontWeight: 500 }}>Цена (€)</Typography>
                  <Slider
                    value={priceRange}
                    onChange={(e, newValue) => setPriceRange(newValue)}
                    valueLabelDisplay="auto"
                    min={0}
                    max={500000}
                    step={1000}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{priceRange[0].toLocaleString()} €</Typography>
                    <Typography variant="body2">{priceRange[1].toLocaleString()} €</Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography gutterBottom sx={{ fontWeight: 500 }}>Тип на возило</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {carTypes.map((type) => (
                      <FormControlLabel
                        key={type}
                        control={
                          <Checkbox 
                            size="small"
                            checked={selectedTypes.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTypes([...selectedTypes, type]);
                              } else {
                                setSelectedTypes(selectedTypes.filter(t => t !== type));
                              }
                            }}
                          />
                        }
                        label={<Typography variant="body2">{type}</Typography>}
                      />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Град</InputLabel>
                    <Select
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      label="Град"
                    >
                      <MenuItem value="">Сите градови</MenuItem>
                      {cities.map((city) => (
                        <MenuItem key={city} value={city}>{city}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Менувач</InputLabel>
                    <Select
                      value={transmission}
                      onChange={(e) => setTransmission(e.target.value)}
                      label="Менувач"
                    >
                      <MenuItem value="">Сите типови</MenuItem>
                      {transmissions.map((trans) => (
                        <MenuItem key={trans} value={trans}>{trans}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Состојба</InputLabel>
                    <Select
                      value={carStatus}
                      onChange={(e) => setCarStatus(e.target.value)}
                      label="Состојба"
                    >
                      <MenuItem value="">Сите</MenuItem>
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={() => {
                    setPriceRange([0, 100000]);
                    setSelectedTypes([]);
                    setCity('');
                    setTransmission('');
                    setCarStatus('');
                  }}
                >
                  Ресетирај филтри
                </Button>
              </Paper>

              {/* Vertical Ad Space */}
              <AdSpace size="160x600 (Skyscraper)" vertical />
            </Box>
          </Grid>
        )}

        {/* Vehicle Listings */}
        <Grid item xs={12} md={isMobile ? 12 : 6}>
          {/* Results Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 4
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {filteredCars.length} {filteredCars.length === 1 ? 'автомобил' : 'автомобили'} пронајдени
            </Typography>
          </Box>

          {loading ? (
            <LoadingSkeleton />
          ) : filteredCars.length === 0 ? (
            <Paper elevation={0} sx={{ 
              p: 8, 
              textAlign: 'center',
              borderRadius: 2,
              bgcolor: 'rgba(0,0,0,0.02)'
            }}>
              <DirectionsCar sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Нема премиум огласи</Typography>
              <Typography color="text.secondary">Проверете повторно подоцна или пребарајте во сите огласи</Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredCars.map((car, index) => (
                <React.Fragment key={car.id}>
                  <Grid item xs={12} sm={6} lg={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 10px 20px rgba(0,0,0,0.08)'
                        }
                      }}
                    >
                      {car.isUrgent && (
                        <Box sx={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <Whatshot color="error" />
                          <Chip
                            label="ИТНО"
                            size="small"
                            color="error"
                            sx={{ ml: 1, fontWeight: 700 }}
                          />
                        </Box>
                      )}

                      <Box sx={{ 
                        position: 'relative',
                        height: 180,
                        overflow: 'hidden'
                      }}>
                        <img
                          src={car.images[car.coverPhotoIndex || 0]}
                          alt={car.model}
                          style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s ease'
                          }}
                        />
                        <Box sx={{ 
                          position: 'absolute',
                          bottom: 10,
                          left: 10
                        }}>
                          <Chip
                            icon={<Star sx={{ fontSize: '16px' }} />}
                            label="Премиум"
                            size="small"
                            sx={{ 
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ p: 2.5, flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700,
                          mb: 0.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {car.brand} {car.model}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          mb: 2,
                          fontSize: '0.875rem'
                        }}>
                          {car.year} • {car.fuelType} • {car.transmission}
                        </Typography>

                        <Box sx={{ 
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: 1.5,
                          mb: 3
                        }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarToday fontSize="small" color="action" />
                            <Typography variant="body2">{car.year}</Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Speed fontSize="small" color="action" />
                            <Typography variant="body2">{(car.mileage/1000).toFixed(0)}k км</Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LocalGasStation fontSize="small" color="action" />
                            <Typography variant="body2">{car.fuelType}</Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LocationOn fontSize="small" color="action" />
                            <Typography variant="body2">{car.city || 'Непознато'}</Typography>
                          </Stack>
                        </Box>

                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          mt: 'auto'
                        }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Цена
                            </Typography>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 800,
                              color: theme.palette.primary.main
                            }}>
                              {car.price.toLocaleString()} €
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => navigate(`/cars/${car.id}`)}
                            sx={{ 
                              fontWeight: 600,
                              px: 2.5,
                              py: 1,
                              borderRadius: 1,
                              minWidth: 120,
                              background: 'linear-gradient(to right, #2b2d42, #4a4e69)',
                              '&:hover': {
                                background: 'linear-gradient(to right, #3a3d5d, #5b5f7d)'
                              }
                            }}
                          >
                            Детали
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Insert Ad after every 4th car */}
                  {(index + 1) % 4 === 0 && (
                    <Grid item xs={12} sx={{ mt: 2, mb: 4 }}>
                      <AdSpace size="728x90 (Leaderboard)" />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          )}
        </Grid>

        {/* Right Sidebar with Ad - Desktop Only */}
        {!isMobile && (
          <Grid item md={3}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              <AdSpace size="160x600 (Skyscraper)" vertical />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Bottom Ad Banner */}
      <Box sx={{ mt: 8 }}>
        <AdSpace size="970x90 (Large Leaderboard)" />
      </Box>
    </Container>
  );
};

export default PremiumListings;